import React from "react";

import { Icon, getRange } from "aible";

import styles from "./style.module.css";
import numeral from "numeral";

import { AutoSizer } from "react-virtualized";
import { BarChart, ResponsiveContainer, Line, LineChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

import { FAIRNESS_OBJECTIVE } from "containers/Project/reducer";
import { ALERT_TYPES, MONITOR_VIEW_MODES, NEXT_VIEW_MODES_LIST } from "containers/Monitoring/reducer";

const DATA_EVAL_COLORS = {
  NOT_COMPLIED: "#f8cd7e",

  COMPLIED: "#54C06C"
};

const convertToPercentage = (num, sigFigs = 2) => {
  let formatString = "0.[0";

  for (let i = 1; i < sigFigs; i++) {
    formatString += "0";
  }

  formatString += "]";

  return numeral(num * 100).format(formatString) + "%";
};

const CustomToolTip = (props) => {
  const { active, payload, setupLabels, isMini, tooltipFollowKey, tooltipNotFollowKey, dataKey, fairnessWarnings } =
    props;

  const currPayload = payload && payload[0] && payload[0].payload;

  if (!active || !currPayload) {
    return null;
  }

  return (
    <div className={styles.cTooltip} style={{ fontSize: "14px" }}>
      <div className={styles.cHeader}>{currPayload.date}</div>
      <table className={styles.fairnessTable}>
        <tbody>
          {currPayload?.segmentId.map(
            (sId) =>
              !isNaN(currPayload[`${sId}_value_cumulative`]) && (
                <tr key={"table_row_" + sId}>
                  <td className={styles.tLabel}>
                    <div className={styles.tLegendIndicator} style={{ background: currPayload?.colors[sId] }} />
                    <span>{currPayload?.labels[sId]}</span>
                  </td>
                  <td>
                    <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "center", width: "100%" }}>
                      {fairnessWarnings.find((elem) => elem.segmentId === sId && elem.warning) ? (
                        <Icon type={"warning"} />
                      ) : (
                        <React.Fragment />
                      )}
                      <span style={{ marginLeft: "6px" }}>
                        {convertToPercentage(currPayload[`${sId}_value_cumulative`])}
                      </span>
                    </div>
                  </td>
                </tr>
              )
          )}
        </tbody>
      </table>
    </div>
  );
};

export default class FairnessResults extends React.PureComponent {
  getTitle(titleOverride) {
    const { setupLabels, fairnessObjective, fairnessToleranceBound } = this.props;
    let retTitle = titleOverride !== undefined ? titleOverride : "Monitoring Fairness";
    switch (fairnessObjective) {
      case FAIRNESS_OBJECTIVE.TRUE_POSITIVE_RATE:
        retTitle += `: Equal ${setupLabels.truePositiveLabel} Rate`;
        break;
      case FAIRNESS_OBJECTIVE.ALLOCATION:
        retTitle += `: Representative Share`;
        break;
      case FAIRNESS_OBJECTIVE.FALSE_POSITIVE_RATE:
        retTitle += `: Equal ${setupLabels.falsePositiveLabel} Rate`;
        break;
      case FAIRNESS_OBJECTIVE.ODDS:
        retTitle += `: Equal Odds`;
        break;
      case FAIRNESS_OBJECTIVE.ACCURACY:
        retTitle += `: Equal Accuracy`;
        break;
      case FAIRNESS_OBJECTIVE.NONE:
      default:
        break;
    }
    return retTitle;
  }

  renderHeader() {
    const { fairnessWarnings, isMini, fairnessToleranceBound } = this.props;

    if (isMini) {
      if (fairnessWarnings.some((e) => e.warning)) {
        return (
          <div className={styles.fairnessLegendWrapper} style={{ backgroundColor: "#F8CD7E", alignItems: "center" }}>
            <Icon type={"warning"} size={36} />
            <div className={styles.fairnessMiniWarning}>{this.getTitle("Fairness Issue Detected")}</div>
            <LegendItem
              label={"Target Max Difference"}
              value={fairnessToleranceBound / 100}
              color={"#000000"}
              isWarning
              isMini
            />
          </div>
        );
      } else {
        return (
          <div className={styles.fairnessTitle} style={{ paddingLeft: "15px" }}>
            {this.getTitle()}
          </div>
        );
      }
    }

    let showDate = fairnessWarnings.some((e) => e.date !== fairnessWarnings[0]?.date);

    return (
      <React.Fragment>
        <div className={styles.fairnessTitle} style={{ paddingLeft: "15px" }}>
          {this.getTitle()}
        </div>
        <div className={styles.fairnessLegendWrapper}>
          <LegendItem label={"Target Max Difference"} value={fairnessToleranceBound / 100} color={"transparent"} />
          {fairnessWarnings.map((subgroupItem) => (
            <LegendItem
              key={subgroupItem?.label + " _Legend"}
              label={subgroupItem?.label}
              value={subgroupItem?.value}
              color={subgroupItem?.color}
              isWarning={subgroupItem?.warning}
              date={showDate ? subgroupItem?.date : undefined}
            />
          ))}
        </div>
      </React.Fragment>
    );
  }

  renderFooter() {
    const { updateMonitorViewMode } = this.props;

    return (
      <div className={styles.fairnessFooterWrapper}>
        <a onClick={() => updateMonitorViewMode(MONITOR_VIEW_MODES.FAIRNESS)} className={styles.learnMoreLink}>
          Learn More
        </a>
      </div>
    );
  }

  render() {
    const { fairnessPlot, fairnessWarnings, setupLabels, isMini } = this.props;

    if (!fairnessPlot || !fairnessWarnings || !fairnessWarnings.length) {
      return <React.Fragment />;
    }

    return (
      <div className={styles.fairnessWrapper}>
        {this.renderHeader()}
        <div className={styles.fairnessBody}>
          <ResponsiveContainer height={400}>
            <LineChart
              width={500}
              height={300}
              data={fairnessPlot}
              margin={{
                top: 0,
                right: 30,
                left: 20,
                bottom: 5
              }}
            >
              {!isMini && <CartesianGrid strokeDasharray="3 3" />}
              <XAxis dataKey="date" />
              <YAxis
                tickFormatter={(tick) => convertToPercentage(tick)}
                domain={[
                  (dataMin) => {
                    const retMin = Math.max(Math.floor(dataMin * 10) / 10, 0);
                    return isNaN(retMin) ? dataMin : retMin;
                  },
                  (dataMax) => {
                    const retMax = Math.min(Math.ceil(dataMax * 10) / 10, 1);
                    return isNaN(retMax) ? dataMax : retMax;
                  }
                ]}
              />
              <Tooltip content={<CustomToolTip setupLabels={setupLabels} fairnessWarnings={fairnessWarnings} />} />
              {fairnessWarnings.map((subgroupItem) => (
                <Line
                  key={`${subgroupItem.label}_graph`}
                  dataKey={`${subgroupItem.segmentId}_value_cumulative`}
                  stroke={subgroupItem.color}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
        {isMini ? this.renderFooter() : <React.Fragment />}
      </div>
    );
  }
}

class LegendItem extends React.PureComponent {
  renderValue() {
    const { color, label, value, valueDiff, isMini, date } = this.props;
    return (
      <React.Fragment>
        <div className={styles.fairnessLegendTitle + " " + (isMini ? styles.isMini : "")}>
          {convertToPercentage(value)}
        </div>
        {valueDiff !== undefined ? (
          <div className={styles.userComplianceLegendSubTitle + " " + (isMini ? styles.isMini : "")}>
            {(valueDiff >= 0 ? "+" : "") + convertToPercentage(valueDiff, isMini ? 1 : 2)}
          </div>
        ) : undefined}
      </React.Fragment>
    );
  }

  render() {
    const { color, label, value, isWarning, isMini, date } = this.props;
    if (isMini) {
      return (
        <div className={styles.userComplianceLegendWrapper}>
          <div className={styles.miniFairnessLegend} style={{ borderBottomColor: color }}>
            {this.renderValue()}
          </div>
          {isMini && isWarning ? <div className={styles.infoItemSubtitle}>{label}</div> : <React.Fragment />}
        </div>
      );
    }
    return (
      <div className={styles.fairnessLegendItemWrapper + " " + (isWarning ? styles.isWarning : "")}>
        <div className={styles.fairnessLegendItem} style={{ borderBottomColor: color }}>
          <div className={styles.userComplianceLegendItem}>
            <div className={styles.userComplianceLegendLabel}>{label}</div>
          </div>
          {this.renderValue()}
        </div>
        {isWarning ? (
          <div className={styles.fairnessSubLabel}>
            <Icon type={"warning"} />
            <span style={{ marginLeft: "8px" }}>{"Fairness Issue Detected"}</span>
          </div>
        ) : date !== undefined ? (
          <div className={styles.fairnessSubLabel}>
            <span>{date}</span>
          </div>
        ) : (
          <React.Fragment />
        )}
      </div>
    );
  }
}
